import CustomDatePicker from "@/components/CustomDatePicker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import FormModal from "@/components/v2/molecules/admin/shared/FormModal";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useBookingByShortId } from "@/hooks/data/useBookingByShortId";
import { useBookings } from "@/hooks/useBookings";
import { useCreateIncome, useUpdateIncome } from "@/hooks/admin/shared/useIncomeMutations";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { parseDateFromDatabase } from "@/utils/dateParser";
import { Check, ChevronsUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface IncomeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingIncome?: any;
  defaultValues?: Partial<IncomeFormData>;
}

const incomeSchema = z.object({
  booking_id: z.string().min(1, "Reservación es requerida"),
  income_type: z.enum([
    "Hospedaje",
    "Early Checkin",
    "Late Checkout",
    "Limpieza adicional",
    "Servicios",
    "Otro",
  ]),
  payment_type: z.enum(["Transferencia", "Efectivo"]),
  gross_payment: z.number().min(0, "Pago bruto debe ser mayor a 0"),
  iva: z.number().optional(),
  ret_iva: z.number().optional(),
  ret_isr: z.number().optional(),
  comission: z.number().optional(),
  payout: z.number().min(0, "Pago recibido es requerido"),
  payout_date: z.date(),
  fiscal_invoice: z.boolean(),
  mark_as_paid: z.boolean(),
});

type IncomeFormData = z.infer<typeof incomeSchema>;

const IncomeFormModal: React.FC<IncomeFormModalProps> = ({
  isOpen,
  onClose,
  editingIncome,
  defaultValues,
}) => {
  const [bookingSearchOpen, setBookingSearchOpen] = useState(false);
  const [bookingSearchValue, setBookingSearchValue] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState("");

  const { data: bookings } = useBookings();
  const { data: selectedBooking } = useBookingByShortId(selectedBookingId);
  const createIncome = useCreateIncome();
  const updateIncome = useUpdateIncome();

  const form = useForm<IncomeFormData>({
    resolver: zodResolver(incomeSchema),
    defaultValues: {
      booking_id: defaultValues?.booking_id || "",
      income_type: defaultValues?.income_type || "Hospedaje",
      payment_type: defaultValues?.payment_type || "Transferencia",
      gross_payment: defaultValues?.gross_payment || 0,
      iva: defaultValues?.iva || 0,
      ret_iva: defaultValues?.ret_iva || 0,
      ret_isr: defaultValues?.ret_isr || 0,
      comission: defaultValues?.comission || 0,
      payout: defaultValues?.payout || 0,
      payout_date: new Date(),
      fiscal_invoice: false,
      mark_as_paid: false,
    },
  });

  const watchedIncomeType = form.watch("income_type");

  // Cargar estado inicial del checkbox cuando selectedBooking cambia (solo para edición)
  const [initialMarkAsPaidLoaded, setInitialMarkAsPaidLoaded] = useState(false);

  useEffect(() => {
    if (editingIncome && isOpen && selectedBooking && !initialMarkAsPaidLoaded) {
      const isCurrentlyPaid = selectedBooking.payment_status === 'paid';
      form.setValue('mark_as_paid', isCurrentlyPaid);
      setInitialMarkAsPaidLoaded(true);
    }
  }, [editingIncome, isOpen, selectedBooking, initialMarkAsPaidLoaded, form]);

  useEffect(() => {
    if (editingIncome && isOpen) {
      setSelectedBookingId(editingIncome.booking_id);
      setInitialMarkAsPaidLoaded(false); // Reset para que se cargue cuando selectedBooking esté listo

      form.reset({
        booking_id: editingIncome.booking_id,
        income_type: editingIncome.income_type,
        payment_type: editingIncome.payment_type,
        gross_payment: editingIncome.gross_payment,
        iva: editingIncome.iva || 0,
        ret_iva: editingIncome.ret_iva || 0,
        ret_isr: editingIncome.ret_isr || 0,
        comission: editingIncome.comission || 0,
        payout: editingIncome.payout,
        payout_date: parseDateFromDatabase(editingIncome.payout_date) || new Date(),
        fiscal_invoice: editingIncome.fiscal_invoice,
        mark_as_paid: false, // Se actualizará cuando selectedBooking cargue
      });
    } else if (!editingIncome && isOpen && defaultValues) {
      if (defaultValues.booking_id) {
        setSelectedBookingId(defaultValues.booking_id);
      }
      setInitialMarkAsPaidLoaded(false);
      form.reset({
        booking_id: defaultValues.booking_id || "",
        income_type: defaultValues.income_type || "Hospedaje",
        payment_type: defaultValues.payment_type || "Transferencia",
        gross_payment: defaultValues.gross_payment || 0,
        iva: defaultValues.iva || 0,
        ret_iva: defaultValues.ret_iva || 0,
        ret_isr: defaultValues.ret_isr || 0,
        comission: defaultValues.comission || 0,
        payout: defaultValues.payout || 0,
        payout_date: defaultValues.payout_date || new Date(),
        fiscal_invoice: defaultValues.fiscal_invoice || false,
        mark_as_paid: false,
      });
    } else if (!editingIncome && isOpen) {
      setSelectedBookingId("");
      setInitialMarkAsPaidLoaded(false);
      form.reset();
    }
  }, [editingIncome, isOpen, defaultValues, form]);

  const filteredBookings =
    bookings?.filter((booking) =>
      booking.booking_id
        .toLowerCase()
        .includes(bookingSearchValue.toLowerCase()),
    ) || [];

  const onSubmit = async (data: IncomeFormData) => {
    try {
      const payload = {
        booking_id: data.booking_id,
        income_type: data.income_type,
        payment_type: data.payment_type,
        gross_payment: data.gross_payment,
        iva: data.iva || 0,
        ret_iva: data.ret_iva || 0,
        ret_isr: data.ret_isr || 0,
        comission: data.comission || 0,
        payout: data.payout,
        payout_date: format(data.payout_date, "yyyy-MM-dd"),
        fiscal_invoice: data.fiscal_invoice,
        mark_as_paid: data.income_type === "Hospedaje" ? data.mark_as_paid : undefined,
      };

      if (editingIncome) {
        await updateIncome.mutateAsync({ id: editingIncome.id, data: payload });
      } else {
        await createIncome.mutateAsync(payload);
      }

      onClose();
    } catch (error) {
      console.error("Error saving income:", error);
    }
  };

  const handleBookingSelect = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    form.setValue("booking_id", bookingId);
    setBookingSearchOpen(false);
  };

  const isHospedaje = watchedIncomeType === "Hospedaje";

  return (

    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={editingIncome ? "Editar Ingreso" : "Crear Nuevo Ingreso"}
      form={form}
      onSubmit={onSubmit}
      isLoading={createIncome.isPending || updateIncome.isPending}
      submitLabel={editingIncome ? "Actualizar Ingreso" : "Crear Ingreso"}
      maxWidth="max-w-2xl"
    >
      <div onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
          e.preventDefault();
        }
      }}>
        {/* Reservación */}
        <FormField
          control={form.control}
          name="booking_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Reservación</FormLabel>

              <Popover
                open={bookingSearchOpen}
                onOpenChange={setBookingSearchOpen}
              >
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "justify-between",
                        !field.value && "text-muted-foreground",
                      )}
                    >
                      {field.value || "Seleccionar reservación..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>

                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput
                      placeholder="Buscar reservación..."
                      value={bookingSearchValue}
                      onValueChange={setBookingSearchValue}
                    />

                    <CommandList>
                      <CommandEmpty>
                        No se encontraron reservaciones.
                      </CommandEmpty>

                      <CommandGroup>
                        {filteredBookings.map((booking) => (
                          <CommandItem
                            key={booking.id}
                            value={booking.booking_id}
                            onSelect={() =>
                              handleBookingSelect(booking.booking_id)
                            }
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                field.value === booking.booking_id
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <div>
                              <div className="font-medium">
                                {booking.booking_id}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {booking.guest_name} •{" "}
                                {format(
                                  parseISO(booking.check_in),
                                  "dd/MM/yy",
                                )}{" "}
                                -{" "}
                                {format(
                                  parseISO(booking.check_out),
                                  "dd/MM/yy",
                                )}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {selectedBooking && (
                <div className="p-2 bg-muted rounded-md text-sm">
                  <div className="font-medium">
                    {selectedBooking.guest_name}
                  </div>

                  <div className="text-muted-foreground">
                    {format(
                      parseISO(selectedBooking.check_in),
                      "dd/MM/yy",
                    )}{" "}
                    -{" "}
                    {format(
                      parseISO(selectedBooking.check_out),
                      "dd/MM/yy",
                    )}
                  </div>
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Línea 1: Tipo de Ingreso + Tipo de Pago */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <FormField
            control={form.control}
            name="income_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Ingreso</FormLabel>

                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    <SelectItem value="Hospedaje">Hospedaje</SelectItem>
                    <SelectItem value="Early Checkin">Early Checkin</SelectItem>
                    <SelectItem value="Late Checkout">Late Checkout</SelectItem>
                    <SelectItem value="Limpieza adicional">Limpieza adicional</SelectItem>
                    <SelectItem value="Servicios">Servicios</SelectItem>
                    <SelectItem value="Otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="payment_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Pago</FormLabel>

                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    <SelectItem value="Transferencia">Transferencia</SelectItem>
                    <SelectItem value="Efectivo">Efectivo</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Línea 2: Pago Bruto + IVA */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <FormField
            control={form.control}
            name="gross_payment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pago Bruto</FormLabel>

                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="iva"
            render={({ field }) => (
              <FormItem>
                <FormLabel>IVA</FormLabel>

                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Línea 3: Retención IVA + Retención ISR (solo Hospedaje) */}
        {isHospedaje && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            <FormField
              control={form.control}
              name="ret_iva"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Retención IVA</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ret_isr"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Retención ISR</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Línea 4: Comisión (solo Hospedaje) */}
        {isHospedaje && (
          <div className="mt-4">
            <FormField
              control={form.control}
              name="comission"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comisión</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Línea 5: Pago Recibido + Fecha de Pago */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <FormField
            control={form.control}
            name="payout"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pago Recibido</FormLabel>

                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="payout_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fecha de Pago</FormLabel>

                <FormControl>
                  <CustomDatePicker
                    selected={field.value}
                    onSelect={field.onChange}
                    placeholder="Seleccionar fecha"
                    className="w-full h-fit"
                    isAdminContext={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Línea 6: Requiere Factura + Reservación Pagada (solo Hospedaje) */}
        <div className="flex flex-row items-center gap-8 mt-4 mb-2">
          <FormField
            control={form.control}
            name="fiscal_invoice"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-normal cursor-pointer">Requiere Factura</FormLabel>
              </FormItem>
            )}
          />

          {isHospedaje && (
            <FormField
              control={form.control}
              name="mark_as_paid"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="font-normal cursor-pointer">Reservación pagada</FormLabel>
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default IncomeFormModal;
